// @ts-check
import { values, keyBy, forEach, groupBy, map } from "lodash";

import { reportError } from "src/services/errorReporting";

import {
  getForecastColumns,
  getForecastMacroAllocations,
  setGroupingsWithActualConstraints,
  setGroupingsWithDefaultConstraints,
  enableSupplyGroupingForecastTargets,
  setGroupingsWithForecastCells,
  setGroupingsWithForecastDefaultCells,
  setCalculatedSumTotals,
  setGroupingsTotalCost,
  setNotifications,
  pruneEmptyGroupings,
  buildGroupingLookup,
  buildCellLookup,
  hashGrouping,
  setGroupingsBudget,
  buildGroupLookup,
  buildLineItemConstraintLookups,
} from "../util";

import buildGroupings from "./buildGroupings/bySupply";
import rebuildWithExtraDemandGroupings from "./buildGroupings/rebuildWithExtraDemandGrouping";
import setGroupingsWithLineItems from "./setLineItems/demandLineItems";
import setGroupingsWithIndirectForecastLineItems from "./setLineItems/indirectForecastLineItems";
import filterDemandRollupContextMacroAllocations from "../util/filterDemandRollupContextMacroAllocations";

const buildGroupedBySupply = ({
  rootGroup,
  data,
  groupTypes,
  insertExtraDemandGroupings = false,
  hideHiddenTeams,
  showObjectives,
  shouldHash,
  isBudgetEnabled,
  overBudgetLimit,
  underBudgetLimit,
  enableDemandRequest = false,
  enableDemandRollupPlanning = false,
  enableSupplyForecastTargets = false,
}) => {
  // Handle null/undefined data gracefully
  if (!data) {
    const error = new Error(
      "buildGroupedBySupply: data is null or undefined. Forecast query data may not have loaded yet."
    );
    reportError(error, {
      rootGroupId: rootGroup?.id,
      rootGroupName: rootGroup?.name,
      viewMode: "GROUPED_BY_SUPPLY",
    });

    // Return safe default structure to prevent further errors
    return {
      columns: [],
      groupings: [],
      lookups: {
        allocationProjectLookup: {},
        columnLookup: {},
        groupingLookup: {},
        lineItemLookup: {},
        cellLookup: {},
        constraintCellLookup: {},
      },
    };
  }

  // Ensure allocationProjects exists, default to empty array
  const allocationProjects = data.allocationProjects || [];
  const columns = getForecastColumns(allocationProjects);
  const allocationProjectLookup = keyBy(
    map(columns, (c) => c.allocationProject),
    "id"
  );
  const groupLookup = buildGroupLookup({
    macroAllocationGroups: data.macroAllocationGroups || [],
    descendantSupplyGroups: data.descendantSupplyGroups || [],
    descendantTargetGroups: data.descendantTargetGroups || [],
    relatedBudgetTargetGroups: data.relatedBudgetTargetGroups || [],
    roleAreaGroups: data.roleAreaGroups || [],
    relatedSupplyGroups: data.relatedSupplyGroups || [],
    groupTypes,
  });
  const constraints = groupBy(data.constraints || [], "groupId");
  const filteredMacroAllocations = filterDemandRollupContextMacroAllocations({
    macroAllocations: data.macroAllocations || [],
    groupLookup,
    groupTypes,
    enableDemandRollupPlanning,
  });

  const macroAllocations = getForecastMacroAllocations({
    macroAllocations: filteredMacroAllocations,
    allocationProjectLookup,
    groupedByDemand: false,
  });

  const groupings = buildGroupings({
    rootGroup,
    groupLookup,
    hideHiddenTeams,
    showObjectives,
    groupTypes,
    columnCount: columns.length,
    isBudgetEnabled,
    allocationProjectLookup,
  });

  // Building demand line items, dailyRate should inherit from parentGrouping
  setGroupingsWithLineItems({
    groupings,
    groupLookup,
    macroAllocations,
    columnCount: columns.length,
    groupTypes,
    canRename: !rootGroup.isSource,
    canHide: !rootGroup.isSource,
    isBudgetEnabled,
    hideHiddenTeams,
  });
  setGroupingsWithForecastCells({
    groupings,
    macroAllocations,
    columns,
    groupedByDemand: false,
    rootGroup,
    enableDemandRequest,
  });
  setGroupingsWithForecastDefaultCells(
    groupings,
    columns,
    false,
    rootGroup,
    enableDemandRequest
  );

  if (insertExtraDemandGroupings) {
    // extra business req: add extra hierarchy groups for demand line items.
    rebuildWithExtraDemandGroupings({
      groupings,
      showObjectives,
      groupTypes,
      isBudgetEnabled,
      allocationProjectLookup,
      groupLookup,
      enableDemandRollupPlanning,
    });
  }

  if (isBudgetEnabled) {
    setGroupingsBudget({ groupings, allocationProjectLookup });
    setGroupingsTotalCost(groupings, overBudgetLimit, underBudgetLimit);
  }

  setCalculatedSumTotals(groupings, columns);
  if (enableSupplyForecastTargets) {
    enableSupplyGroupingForecastTargets(groupings, columns.length);
  }
  setGroupingsWithActualConstraints({
    groupings,
    constraints,
    allocationProjectLookup,
    columns,
  });
  setGroupingsWithDefaultConstraints({
    groupings,
    allocationProjectLookup,
    columns,
  });
  setNotifications(groupings, columns);
  // pruneEmptyLineItems(groupings);
  pruneEmptyGroupings(groupings, isBudgetEnabled);

  // Add indirect placeholder forecast lineitems for requestor side
  if (rootGroup.isDemand) {
    setGroupingsWithIndirectForecastLineItems({
      groupings,
      columns,
      groupTypes,
      rootGroup,
      isBudgetEnabled,
    });
  }

  const columnLookup = keyBy(columns, "id");
  const groupingLookup = buildGroupingLookup(groupings, {});
  const { lineItemLookup, constraintCellLookup } =
    buildLineItemConstraintLookups(values(groupingLookup), {}, {});
  const cellLookup = buildCellLookup(values(lineItemLookup), {});
  // Hash groupings in case anything changed
  if (shouldHash) {
    forEach(groupings, hashGrouping);
  }

  return {
    columns,
    groupings,
    // lookup objects: for when we need fast access to objects in the tree
    lookups: {
      allocationProjectLookup,
      columnLookup,
      groupLookup,
      groupingLookup,
      lineItemLookup,
      cellLookup,
      constraintCellLookup,
      macroAllocationLookup: macroAllocations,
    },
  };
};

export default buildGroupedBySupply;
